import { useEffect, useMemo, useState } from "react";
import { Download, RefreshCw, Filter, Loader2, TrendingUp, TrendingDown, ShieldAlert, Users } from "lucide-react";
import { toast } from "sonner";
import api, { downloadAuthedFile } from "@/lib/api";

const EUR = (v) => new Intl.NumberFormat("pt-PT", {
  style: "currency", currency: "EUR",
}).format(Number(v || 0));

const FMT_DATE = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-PT", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return String(iso).slice(0, 10); }
};

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * AcceptedMargins — internal panel showing every accepted proposal with
 * revenue, authorized AF cost and margin. Admins see everything; consultores
 * see only proposals they own. Export as A4 PDF respects the same scoping.
 */
export default function AcceptedMargins() {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const [dateFrom, setDateFrom] = useState(toYMD(monthStart));
  const [dateTo, setDateTo] = useState(toYMD(today));
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({
    count: 0, monthly_revenue: 0, monthly_af_cost: 0,
    monthly_absolute_margin: 0, avg_margin_pct: 0,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/margins/accepted", {
        params: { from: dateFrom || undefined, to: dateTo || undefined },
      });
      setItems(data.items || []);
      setTotals(data.totals || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a carregar margens");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadPdf = async () => {
    const qs = new URLSearchParams();
    if (dateFrom) qs.set("from", dateFrom);
    if (dateTo) qs.set("to", dateTo);
    const label = dateFrom && dateTo
      ? `_${dateFrom}_a_${dateTo}` : dateFrom
      ? `_desde_${dateFrom}` : dateTo ? `_ate_${dateTo}` : "";
    try {
      setLoading(true);
      await downloadAuthedFile(
        `/margins/accepted/pdf?${qs.toString()}`,
        `Margens-Aceites${label}.pdf`,
      );
    } catch (e) {
      toast.error("Erro a gerar PDF");
    } finally { setLoading(false); }
  };

  const marginTone = (row) => (!row.threshold_ok)
    ? "text-[hsl(var(--danger))]"
    : "text-[hsl(var(--success))]";

  const overallTone = totals.avg_margin_pct >= 30
    ? "text-[hsl(var(--success))]"
    : totals.avg_margin_pct >= 15
      ? "text-[hsl(var(--warning))]"
      : "text-[hsl(var(--danger))]";

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="page-accepted-margins">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-heading font-semibold mb-1">
            Painel interno · Margens
          </div>
          <h1 className="font-heading text-2xl tracking-tight text-foreground">
            Propostas Aceites · Margens
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Receita, custo AFs autorizado (fixado na proposta) e margem por
            período. <b>Este dado é interno</b> — nunca aparece no PDF ou email
            do cliente.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap" data-testid="margins-filters">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-heading font-semibold mb-1">
              Desde
            </div>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="margins-date-from"
              className="h-9 px-3 border border-border rounded-md text-sm bg-card focus:outline-none focus:ring-2 focus:ring-primary/25"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-heading font-semibold mb-1">
              Até
            </div>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="margins-date-to"
              className="h-9 px-3 border border-border rounded-md text-sm bg-card focus:outline-none focus:ring-2 focus:ring-primary/25"
            />
          </label>
          <button
            onClick={fetchData}
            disabled={loading}
            data-testid="margins-refresh"
            className="h-9 px-3 rounded-md text-sm bg-foreground text-background hover:bg-[#1E293B] inline-flex items-center gap-1.5 font-heading disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Filter className="w-3.5 h-3.5" />}
            Actualizar
          </button>
          <button
            onClick={downloadPdf}
            disabled={loading}
            data-testid="margins-download-pdf"
            className="h-9 px-3 rounded-md text-sm bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-1.5 font-heading disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            Descarregar PDF
          </button>
        </div>
      </div>

      {/* Totals cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="margins-totals">
        <TotalCard
          label="Propostas aceites"
          value={totals.count}
          testid="margins-total-count"
          icon={<Users className="w-3.5 h-3.5" />}
        />
        <TotalCard
          label="Receita mensal"
          value={EUR(totals.monthly_revenue)}
          testid="margins-total-revenue"
          tone="text-[hsl(var(--info))]"
        />
        <TotalCard
          label="Custo AFs"
          value={EUR(totals.monthly_af_cost)}
          testid="margins-total-cost"
          tone="text-foreground"
        />
        <TotalCard
          label="Margem €/mês"
          value={EUR(totals.monthly_absolute_margin)}
          testid="margins-total-margin"
          tone={overallTone}
          icon={totals.monthly_absolute_margin >= 0
            ? <TrendingUp className="w-3.5 h-3.5" />
            : <TrendingDown className="w-3.5 h-3.5" />}
        />
        <TotalCard
          label="Margem média"
          value={`${Number(totals.avg_margin_pct || 0).toFixed(1).replace(".", ",")}%`}
          testid="margins-total-pct"
          tone={overallTone}
        />
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="margins-table">
            <thead className="bg-muted/60 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3 font-heading font-semibold">Data aceite</th>
                <th className="text-left px-4 py-3 font-heading font-semibold">Cliente</th>
                <th className="text-left px-4 py-3 font-heading font-semibold">Consultor(a)</th>
                <th className="text-left px-4 py-3 font-heading font-semibold">Categoria</th>
                <th className="text-right px-4 py-3 font-heading font-semibold">Receita</th>
                <th className="text-right px-4 py-3 font-heading font-semibold">Custo AFs</th>
                <th className="text-right px-4 py-3 font-heading font-semibold">Margem €</th>
                <th className="text-right px-4 py-3 font-heading font-semibold">Margem %</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground text-sm">
                    Sem propostas aceites no período seleccionado.
                  </td>
                </tr>
              ) : items.map((row) => (
                <tr
                  key={row.lead_id}
                  data-testid={`margins-row-${row.lead_id}`}
                  className="border-t border-border hover:bg-muted/40 transition-colors"
                >
                  <td className="px-4 py-3 tabular-nums text-muted-foreground">
                    {FMT_DATE(row.accepted_at)}
                  </td>
                  <td className="px-4 py-3 font-medium text-foreground">
                    {row.client_name}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {row.consultor_name}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-semibold">
                      {row.service_category_label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-foreground">
                    {EUR(row.monthly_revenue)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-foreground">
                    {EUR(row.monthly_af_cost)}
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums font-semibold ${marginTone(row)}`}>
                    {EUR(row.monthly_absolute_margin)}
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums font-bold ${marginTone(row)}`}>
                    <span className="inline-flex items-center gap-1.5">
                      {!row.threshold_ok && !row.has_authorization && (
                        <ShieldAlert className="w-3.5 h-3.5" title="Abaixo do mínimo — sem autorização" />
                      )}
                      {!row.threshold_ok && row.has_authorization && (
                        <span title="Abaixo do mínimo — com autorização admin">★</span>
                      )}
                      {`${Number(row.margin_pct).toFixed(1).replace(".", ",")}%`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        <ShieldAlert className="w-3 h-3 inline mr-1" />
        Linhas com <b>★</b> foram autorizadas pelo admin (autorização de margem
        vinculada). Ícone de escudo (⚠) sinaliza margem abaixo do mínimo sem
        autorização — reveja a proposta.
      </p>
    </div>
  );
}

function TotalCard({ label, value, tone = "text-foreground", testid, icon }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-heading font-semibold flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className={`text-xl font-heading font-bold tabular-nums mt-1 ${tone}`}>
        {value}
      </div>
    </div>
  );
}
